import { Dialog } from '@mui/material';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';

import ConsentFormModalContent from '@components/Modal/Content/ConsentModalContent';
import MissingConsentModalContent from '@components/Modal/Content/ConsentModalContent/MissingConsentModalContent';

import { useCheckConsent } from '@hooks/useCheckConsent';
import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import { AccountType } from 'generated/types';

function ConsentOverlay() {
  const {
    state: {
      activeProfile,
      active_user_id,
      logged_in_user_id,
      loggedInProfile,
    },
  } = useSession();
  const {
    state: { patientView },
  } = useCuration();
  const [patient_id, account_type, primary_user_id] = [
    activeProfile?.patient_id,
    loggedInProfile?.account?.account_type,
    loggedInProfile?.account?.owner_id,
  ];
  const [openConsentForm, setOpenConsentForm] = useState(false);
  const [openMissingConsentDialog, setOpenMissingConsentDialog] =
    useState(false);
  const {
    data: { hasConsented },
    loading: consentLoading,
  } = useCheckConsent();

  const fetchDataLoading = consentLoading;

  const handleOpenConsentFormDialog = () => setOpenConsentForm(true);
  const handleCloseConsentFormDialog = () => setOpenConsentForm(false);
  const handleOpenMissingConsentDialog = () =>
    setOpenMissingConsentDialog(true);
  const handleCloseMissingConsentDialog = () =>
    setOpenMissingConsentDialog(false);

  React.useEffect(() => {
    if (!fetchDataLoading) {
      // CASE 1: patient is viewed by ORG user or VITALL user (account_type is organization or vitall)
      // A. User can view patient profile even if their consent is missing (Show alert banner)
      if (
        account_type === AccountType.organization ||
        account_type === AccountType.vitall
      ) {
        return;
      }

      // CASE 2: patient is viewed by NON-ORG user/user within client account_type
      if (!hasConsented) {
        const primaryUserId = primary_user_id;
        /**
         * A. Every Engage user needs to consent: Show consent modal
         * i) Practitioner/Curator/Non-patient user: consents to TOC & PN
         * ii) Patient user viewing their own profile: consents to TOC, PN, and VITALL consent
         */
        if (logged_in_user_id === active_user_id) {
          handleOpenConsentFormDialog();
          return;
        }

        // B. Primary user viewing patient, non-user role: Show consent modal
        if (
          primaryUserId === logged_in_user_id &&
          patient_id &&
          !activeProfile?.email
        ) {
          handleOpenConsentFormDialog();
          return;
        }

        // C. Any other user should not be able to access patient profile if consent is missing
        handleOpenMissingConsentDialog();
        return;
      }
    }

    return () => {
      handleCloseConsentFormDialog();
      handleCloseMissingConsentDialog();
    };
  }, [
    account_type,
    activeProfile?.email,
    active_user_id,
    fetchDataLoading,
    hasConsented,
    logged_in_user_id,
    patientView,
    patient_id,
    primary_user_id,
  ]);

  return (
    <>
      <Dialog open={openConsentForm}>
        <ConsentFormModalContent onClose={handleCloseConsentFormDialog} />
      </Dialog>
      <Dialog open={openMissingConsentDialog}>
        <MissingConsentModalContent onClose={handleCloseMissingConsentDialog} />
      </Dialog>
    </>
  );
}

export default withErrorBoundary(ConsentOverlay, {
  fallback: null,
});
